// src/pages/StoryTreePage.jsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Tree from "react-d3-tree";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../config/firebase";
import StoryTreePagesideber from "../components/Sidebar/StoryTreePagesideber";
import { verifyStoryPassword } from "../modules/firebaseAPI";
import "./ChatPage.css";
import "./StoryTreePage.css";
import AffiliateLinks from "./../modules/amazonAffiliate/AffiliateLinks";
import Seo from "../components/Seo";

// ────────── ツリー構築関数 ──────────
export const buildTreeFromNodes = (nodes) => {
  const rootNode = nodes.find((n) => !n.parentId);
  if (!rootNode) {
    return { name: "ROOT", children: [] };
  }

  const nodeMap = {};
  nodes.forEach((n) => {
    nodeMap[n.id] = {
      id: n.id,
      name: n.text || n.content || "無題",
      children: [],
      parentIdForClick: n.parentId || null,
      category: n.category || "その他",
      title: n.title || "",
    };
  });

  const addedNodes = new Set();
  nodes.forEach((n) => {
    if (Array.isArray(n.childrenIds)) {
      n.childrenIds.forEach((childId) => {
        if (!nodeMap[childId]) return;
        if (addedNodes.has(childId)) return;
        nodeMap[n.id].children.push(nodeMap[childId]);
        addedNodes.add(childId);
      });
    }
  });

  return {
    id: rootNode.id,
    name: rootNode.content || rootNode.text,
    children: nodeMap[rootNode.id]?.children || [],
    parentIdForClick: rootNode.parentId || null,
  };
};

const StoryTreePage = () => {
  const { storyId } = useParams();
  const [treeData, setTreeData] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [currentNode, setCurrentNode] = useState(null);

  // 🔒 パスワード関連
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [requiresPassword, setRequiresPassword] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ────────── データ取得 ──────────
  useEffect(() => {
    if (!storyId) return;

    const fetchAllRelatedNodes = async () => {
      try {
        //console.log("=== 系統樹ロード開始（パスワード対応） ===");

        // ルートストーリー取得
        const rootSnap = await getDocs(
          query(collection(db, "stories"), where("__name__", "==", storyId))
        );
        const rootStory = rootSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))[0];

        if (!rootStory) return;

        // パスワード確認が必要か
        if (rootStory.password) {
          setRequiresPassword(true);
        }

        // パスワード未設定の場合はロード
        if (!rootStory.password) {
          await loadTreeData();
        }
      } catch (err) {
        console.error("系統樹データ取得失敗:", err);
      }
    };

    const loadTreeData = async () => {
      try {
        const colorPalette = [
          "#ffffffff",
          "#e57373",
          "#64b5f6",
          "#81c784",
          "#fff176",
          "#ba68c8",
          "#ffb74d",
          "#4db6ac",
          "#f06292",
          "#9575cd",
          "#aed581",
        ];

        // 🔸 再帰的に全派生ストーリー取得
        const fetchAllStoriesRecursive = async (
          rootId,
          collected = new Set()
        ) => {
          if (collected.has(rootId)) return [];
          collected.add(rootId);

          const rootSnap = await getDocs(
            query(collection(db, "stories"), where("__name__", "==", rootId))
          );
          const rootStories = rootSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));

          const childSnap = await getDocs(
            query(collection(db, "stories"), where("baseStoryId", "==", rootId))
          );
          const childStories = childSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }));

          let all = [...rootStories];
          for (const c of childStories) {
            const descendants = await fetchAllStoriesRecursive(c.id, collected);
            all = [...all, c, ...descendants];
          }
          return all;
        };

        const allStories = await fetchAllStoriesRecursive(storyId);
        /*
        console.log(
          "全ストーリー取得完了:",
          allStories.map((s) => s.id)
        );*/

        const storyColors = {};
        allStories.forEach((story, index) => {
          storyColors[story.id] = colorPalette[index % colorPalette.length];
        });

        // ── 各ストーリーのノード統合
        let allNodes = [];
        for (const story of allStories) {
          const nodeSnap = await getDocs(
            collection(db, `stories/${story.id}/nodes`)
          );
          nodeSnap.forEach((n) => {
            const data = n.data();
            allNodes.push({
              id: n.id,
              ...data,
              storyId: story.id,
              color: storyColors[story.id],
              childrenIds: data.childrenIds || [],
              parentId: data.parentId || null,
              title: story.title,
              category: story.category,
            });
          });
        }

        // ── 仮想リンク多段対応
        const linkNodesRecursively = (nodes) => {
          let updated = false;
          nodes.forEach((node) => {
            if (node.baseStoryId && node.parentNodeId) {
              const parentInBase = nodes.find(
                (n) =>
                  n.id === node.parentNodeId && n.storyId === node.baseStoryId
              );
              if (parentInBase) {
                parentInBase.childrenIds = [
                  ...(parentInBase.childrenIds || []),
                  node.id,
                ];
                node.parentId = parentInBase.id;
                updated = true;
              }
            }
          });
          if (updated) linkNodesRecursively(nodes);
        };

        linkNodesRecursively(allNodes);

        // ── ツリー構築
        const tree = buildTreeFromNodes(allNodes);
        setNodes(allNodes);
        setTreeData(tree);
        setCurrentNode(tree);
      } catch (err) {
        console.error("系統樹ロード失敗:", err);
      }
    };

    fetchAllRelatedNodes();
  }, [storyId]);

  // ────────── パスワード送信処理 ──────────
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const isValid = await verifyStoryPassword(storyId, passwordInput);
    if (isValid) {
      setPasswordVerified(true);
      // 正常ならツリーをロード
      // eslint-disable-next-line no-use-before-define
      loadTreeDataAfterPassword();
    } else {
      alert("パスワードが違います");
    }
  };

  const loadTreeDataAfterPassword = async () => {
    try {
      // fetchAllRelatedNodes と同じ処理をパスワード成功後に呼ぶ
      const colorPalette = [
        "#ffffffff",
        "#e57373",
        "#64b5f6",
        "#81c784",
        "#fff176",
        "#ba68c8",
        "#ffb74d",
        "#4db6ac",
        "#f06292",
        "#9575cd",
        "#aed581",
      ];

      const fetchAllStoriesRecursive = async (
        rootId,
        collected = new Set()
      ) => {
        if (collected.has(rootId)) return [];
        collected.add(rootId);

        const rootSnap = await getDocs(
          query(collection(db, "stories"), where("__name__", "==", rootId))
        );
        const rootStories = rootSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        const childSnap = await getDocs(
          query(collection(db, "stories"), where("baseStoryId", "==", rootId))
        );
        const childStories = childSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        let all = [...rootStories];
        for (const c of childStories) {
          const descendants = await fetchAllStoriesRecursive(c.id, collected);
          all = [...all, c, ...descendants];
        }
        return all;
      };

      const allStories = await fetchAllStoriesRecursive(storyId);

      const storyColors = {};
      allStories.forEach((story, index) => {
        storyColors[story.id] = colorPalette[index % colorPalette.length];
      });

      let allNodes = [];
      for (const story of allStories) {
        const nodeSnap = await getDocs(
          collection(db, `stories/${story.id}/nodes`)
        );
        nodeSnap.forEach((n) => {
          const data = n.data();
          allNodes.push({
            id: n.id,
            ...data,
            storyId: story.id,
            color: storyColors[story.id],
            childrenIds: data.childrenIds || [],
            parentId: data.parentId || null,
            title: story.title,
            category: story.category,
          });
        });
      }

      const linkNodesRecursively = (nodes) => {
        let updated = false;
        nodes.forEach((node) => {
          if (node.baseStoryId && node.parentNodeId) {
            const parentInBase = nodes.find(
              (n) =>
                n.id === node.parentNodeId && n.storyId === node.baseStoryId
            );
            if (parentInBase) {
              parentInBase.childrenIds = [
                ...(parentInBase.childrenIds || []),
                node.id,
              ];
              node.parentId = parentInBase.id;
              updated = true;
            }
          }
        });
        if (updated) linkNodesRecursively(nodes);
      };

      linkNodesRecursively(allNodes);

      const tree = buildTreeFromNodes(allNodes);
      setNodes(allNodes);
      setTreeData(tree);
      setCurrentNode(tree);
    } catch (err) {
      console.error("系統樹ロード失敗:", err);
    }
  };

  // ────────── ノードクリック処理 ──────────
  const handleNodeClick = (nodeDatum) => {
    const nodeData = nodeDatum.data || nodeDatum;
    setCurrentNode(nodeData);
  };

  const renderCustomNode = ({ nodeDatum, toggleNode }, allNodes) => {
    const nodeData = allNodes.find((n) => n.id === nodeDatum.id);
    const color = nodeData?.color || "#ccc";

    return (
      <g
        onClick={(e) => {
          e.stopPropagation();
          toggleNode();
          handleNodeClick(nodeDatum);
        }}
        style={{ cursor: "pointer" }}
      >
        {/* ▼ 影・光沢のフィルター定義 ▼ */}
        <defs>
          <filter id="nodeShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="3"
              floodColor="rgba(0,0,0,0.4)"
            />
          </filter>
        </defs>

        {/* ▼ ノードの丸に filter を適用 ▼ */}
        <circle
          r={20}
          fill={color}
          stroke="#333"
          strokeWidth="2"
          filter="url(#nodeShadow)" // ← これが影を付ける
        />

        <text
          fill="black"
          strokeWidth="0"
          x="-10"
          dy="-30"
          style={{ fontSize: 12 }}
        >
          {nodeDatum.name}
        </text>
      </g>
    );
  };

  // ────────── パスワード入力画面 ──────────
  if (requiresPassword && !passwordVerified) {
    return (
      <div style={{ padding: "20px" }}>
        <div style={{ marginBottom: "20px" }}>
          <h2>パスワードで保護されたストーリーです</h2>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              placeholder="パスワードを入力"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
            />
            <button type="submit">確認</button>
          </form>
        </div>
        <div className="affiliate-group home-section">
          {/* 🔹 AffiliateLinks を表示 */}
          <AffiliateLinks />
        </div>
      </div>
    );
  }

  if (!treeData) return <div>読み込み中...</div>;

  return (
    <>
      {/* 🔹 SEO タグ */}
      <Seo
        title={treeData?.title || "Mind Threat マインドマップ"}
        description={
          treeData?.currentNodeContent
            ? treeData.currentNodeContent.slice(0, 100) + "…"
            : treeData?.title || "Mind Threat マインドマップ"
        }
        image="/siteicon.PNG"
        type="article"
      />

      <div style={{ position: "relative" }}>
        <div className="map-header">
          <h1>マインドマップ</h1>
          <h2>選択したスレッドの他の選択肢を確認することができます</h2>
        </div>

        <div className="storytree-container">
          <button
            className="sidebar-toggle"
            onClick={() => {
              setSidebarOpen(!sidebarOpen);
            }}
          >
            {sidebarOpen ? "×" : "選択した本文を見る"}
          </button>
          <div className="tree-container">
            <Tree
              data={treeData}
              orientation="horizontal"
              collapsible
              pathFunc="diagonal"
              translate={{ x: 100, y: 300 }}
              onNodeClick={handleNodeClick}
              renderCustomNodeElement={(rd3tProps) =>
                renderCustomNode(rd3tProps, nodes)
              }
            />
          </div>

          <StoryTreePagesideber
            className={`storytree-sidebar chat-sidebar ${
              sidebarOpen ? "open" : ""
            }`}
            storyId={storyId}
            story={treeData}
            currentNode={currentNode}
            nodes={nodes}
          />
        </div>
        <div className="affiliate-group home-section">
          {/* 🔹 AffiliateLinks を表示 */}
          <AffiliateLinks />
        </div>
      </div>
    </>
  );
};

export default StoryTreePage;
